export const DESIGN_SIGNED_URL_REFRESH_BUFFER_MS = 5 * 60 * 1000;
export const DESIGN_SIGNED_URL_DEFAULT_REFRESH_MS = 45 * 60 * 1000;

export function isUsableDesignAsset(value: string | null | undefined): value is string {
	if (value == null) {
		return false;
	}
	const normalized = String(value).trim();
	if (!normalized) {
		return false;
	}
	const lowered = normalized.toLowerCase();
	return lowered !== 'null' && lowered !== 'undefined';
}

/**
 * Logo/spinner para UI: base64 de config_design → base64 de config → URL de config_design → URL de config.
 * Base64 se normaliza a `data:`; las URLs se pasan tal cual al `[img]` del componente.
 */
export function resolveBrandingAssetSrc(
	primary: string | null | undefined,
	fallback: string | null | undefined,
): string | null {
	const p = normalizeDesignAssetSrc(primary);
	const f = normalizeDesignAssetSrc(fallback);
	if (p?.startsWith('data:')) {
		return p;
	}
	if (f?.startsWith('data:')) {
		return f;
	}
	return p ?? f ?? null;
}

/** Base64 crudo o URL → src válido para `[img]`. */
export function normalizeDesignAssetSrc(value: string | null | undefined): string | null {
	if (!isUsableDesignAsset(value)) {
		return null;
	}
	const normalized = String(value).trim();
	const lowered = normalized.toLowerCase();
	if (lowered.startsWith('http://') || lowered.startsWith('https://') || lowered.startsWith('data:')) {
		return normalized;
	}
	const mime = detectBase64ImageMime(normalized);
	return mime ? `data:${mime};base64,${normalized}` : normalized;
}

function detectBase64ImageMime(base64: string): string | null {
	if (base64.startsWith('R0lGOD')) return 'image/gif';
	if (base64.startsWith('UklGR')) return 'image/webp';
	if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
	if (base64.startsWith('/9j/')) return 'image/jpeg';
	// SVG: `<?xml` o `<svg` (config_design a veces manda favicon SVG en base64 crudo).
	if (base64.startsWith('PD94bWwg') || base64.startsWith('PHN2Zy')) return 'image/svg+xml';
	return null;
}

export function getSignedUrlExpiryMs(url: string | null | undefined): number | null {
	if (!isUsableDesignAsset(url) || !/^https?:\/\//i.test(String(url).trim())) {
		return null;
	}
	try {
		const parsed = new URL(String(url).trim());
		const amzDate = parsed.searchParams.get('X-Amz-Date');
		const amzExpiresSec = parsed.searchParams.get('X-Amz-Expires');
		if (amzDate && amzExpiresSec) {
			const startMs = parseAmzDateToMs(amzDate);
			const expiresSec = Number(amzExpiresSec);
			if (startMs !== null && Number.isFinite(expiresSec) && expiresSec > 0) {
				return startMs + expiresSec * 1000;
			}
		}
		const expiresParam = parsed.searchParams.get('Expires') ?? parsed.searchParams.get('se');
		if (expiresParam) {
			const asNumber = Number(expiresParam);
			if (Number.isFinite(asNumber) && asNumber > 0) {
				return asNumber < 1e12 ? asNumber * 1000 : asNumber;
			}
		}
	} catch {
		return null;
	}
	return null;
}

export function getDesignAssetRefreshDelayMs(
	logo: string | null | undefined,
	customSpinner: string | null | undefined,
	nowMs: number = Date.now(),
	favicon?: string | null | undefined,
): number | null {
	const expiryMs = [
		getSignedUrlExpiryMs(logo),
		getSignedUrlExpiryMs(customSpinner),
		getSignedUrlExpiryMs(favicon),
	]
		.filter((v): v is number => v !== null)
		.reduce<number | null>((min, v) => (min === null || v < min ? v : min), null);
	if (expiryMs !== null) {
		return Math.max(expiryMs - DESIGN_SIGNED_URL_REFRESH_BUFFER_MS - nowMs, 60_000);
	}
	const hasHttp =
		(isUsableDesignAsset(logo) && /^https?:\/\//i.test(String(logo).trim())) ||
		(isUsableDesignAsset(customSpinner) && /^https?:\/\//i.test(String(customSpinner).trim())) ||
		(isUsableDesignAsset(favicon) && /^https?:\/\//i.test(String(favicon).trim()));
	return hasHttp ? DESIGN_SIGNED_URL_DEFAULT_REFRESH_MS : null;
}

function parseAmzDateToMs(amzDate: string): number | null {
	const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/i.exec(amzDate.trim());
	if (!match) return null;
	const [, y, mo, d, h, mi, s] = match;
	return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
}
